import styled from 'styled-components'
import { useFormikContext } from 'formik'
import { Grid } from '@farewill/ui'
import { COLOR, FONT } from '@farewill/ui/tokens'

import InputFloating from 'components/form/input-floating'
import CircularRadioGroup from 'components/form/circular-radio-group'
import { YES_NO_OPTIONS } from 'utils/enums'

import withForm from 'lib/formik/with-form'
import useValidatedSave from 'lib/formik/use-validated-save'
import { FUNERAL_BILL_SCHEMA } from 'lib/probate/estate-questionnaire-schema'
import { ProbateEstate } from 'lib/models/probate-estate'

import Section from '../components/section'
import useSectionToggle from '../use-section-toggle'

// !important needed to beat CircularRadioGroup's own legend font-weight
const StyledRadioGroup = styled(CircularRadioGroup)`
  legend {
    color: ${COLOR.GREY.DARK};
    font-weight: ${FONT.WEIGHT.MEDIUM} !important;
  }
`

// the radio answer can't be cleared, so it doesn't lock the section
const CLEARABLE_FIELDS = ['funeralBillAmount', 'funeralAmountNotes']

type HandleSave = (event: { name: string; value: unknown }) => void

type Props = {
  probateEstate: ProbateEstate
  saveProbateEstateField: HandleSave
}

const FuneralBillFields = ({
  saveProbateEstateField,
  funeralSectionActive,
}: Pick<Props, 'saveProbateEstateField'> & {
  funeralSectionActive: boolean | undefined
}) => {
  const handleSaveEstateField = useValidatedSave({
    schema: FUNERAL_BILL_SCHEMA,
    save: saveProbateEstateField,
  })

  const toggle = useSectionToggle({
    name: 'funeralSectionActive',
    initialValue: funeralSectionActive,
    save: saveProbateEstateField,
  })

  // the lock reads this form's live values because the estate isn't refetched
  // after a save
  const { values } = useFormikContext<Record<string, unknown>>()
  const hasData = CLEARABLE_FIELDS.some((field) => !!values[field])

  return (
    <Section
      heading="3.2 Funeral bill"
      scrollId="3.2"
      toggle={{ ...toggle, disabled: hasData }}
      notesName="funeralAmountNotes"
      onNotesSave={handleSaveEstateField}
    >
      <Grid.Item span={6}>
        <StyledRadioGroup
          label="Is the funeral bill paid?"
          name="funeralBillPaid"
          options={YES_NO_OPTIONS}
          handleSave={handleSaveEstateField}
          inline
        />
      </Grid.Item>
      <Grid.Item span={6}>
        <InputFloating
          name="funeralBillAmount"
          label="Cost of the funeral (£)"
          handleSave={handleSaveEstateField}
          isCurrency
        />
      </Grid.Item>
    </Section>
  )
}

const FuneralBillForm = withForm(FuneralBillFields)

const FuneralBill = ({ probateEstate, saveProbateEstateField }: Props) => {
  const attrs = probateEstate.attributes

  const initialValues = {
    funeralBillPaid: attrs.funeralBillPaid ?? null,
    funeralBillAmount: attrs.funeralBillAmount ?? null,
    funeralAmountNotes: attrs.funeralAmountNotes ?? null,
  }

  return (
    <FuneralBillForm
      initialValues={initialValues}
      validationSchema={FUNERAL_BILL_SCHEMA}
      saveProbateEstateField={saveProbateEstateField}
      funeralSectionActive={attrs.funeralSectionActive}
    />
  )
}

export default FuneralBill
